import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Toll as TokenIcon,
} from '@mui/icons-material';
import { api, AdminPickerExternalUser } from '../../services/api';
import TokenUsageAnalyticsDialog from './TokenUsageAnalyticsDialog';

/**
 * Admin dialog: list every public share link issued from the agent's
 * `ShareExternalUserDialog` and let admins tweak token allowances.
 *
 * Columns:
 *   - Inviting agent name + CEA number
 *   - Recipient name, mobile, share uuid
 *   - Remaining / Max tokens (inline editable)
 *   - Activated at
 *
 * Backed by:
 *   - GET    /api/admin/picker-external-users
 *   - PATCH  /api/admin/picker-external-users/:id/tokens
 *   - PATCH  /api/admin/picker-external-users/:id/trial
 */

/**
 * Dialog mode:
 * - `admin`: full table for admins. Shows inviting agent columns, allows
 *   editing `MaxTokens` / `RemainingTokens` per row.
 * - `mine`:  current agent's own invitees only. Hides agent columns and
 *   renders tokens as read-only numbers (no inputs, no save action).
 */
export type ManageShareLinksMode = 'admin' | 'mine';

type MineDialogTab = 'shareLinks' | 'tokenUsage';

interface ManageShareLinksDialogProps {
  open: boolean;
  onClose: () => void;
  /** Defaults to `admin` for backwards compatibility with existing callers. */
  mode?: ManageShareLinksMode;
}

const ROWS_PER_PAGE = 25;

type TrialUnit = 'days' | 'hours';

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

/** Prefer whole-day display when the stored minutes divide evenly. */
function trialMinutesToEditState(minutes: number): { amount: string; unit: TrialUnit } {
  if (minutes > 0 && minutes % MINUTES_PER_DAY === 0) {
    return { amount: String(minutes / MINUTES_PER_DAY), unit: 'days' };
  }
  return { amount: String(Math.round(minutes / MINUTES_PER_HOUR)), unit: 'hours' };
}

function formatTrialDuration(minutes: number): string {
  const { amount, unit } = trialMinutesToEditState(minutes);
  const n = Number(amount);
  const label = unit === 'days' ? (n === 1 ? 'day' : 'days') : (n === 1 ? 'hour' : 'hours');
  return `${amount} ${label}`;
}

function parseTrialEdit(amount: string, unit: TrialUnit): number | null {
  const trimmed = amount.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  if (!Number.isInteger(value) || value <= 0) return null;
  return unit === 'days' ? value * MINUTES_PER_DAY : value * MINUTES_PER_HOUR;
}

/**
 * Format an ISO timestamp for the table. Returns an em-dash when the
 * timestamp is null (e.g. share not yet activated).
 */
function formatTimestamp(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-SG', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Display labels for the `Module` enum stored on the entity. Falls back
 * to the raw value if a new module is added that this dialog doesn't yet
 * know about.
 */
const MODULE_LABEL: Record<string, string> = {
  PICKER_SITE_PLANS: 'Site Plans',
  PICKER_PROJECT_DETAILS: 'Project Details',
  PICKER_PROJECT_RESEARCH: 'Project Research',
};

function formatModule(module: string | null | undefined): string {
  if (!module) return '—';
  return MODULE_LABEL[module] ?? module;
}

/**
 * Lifecycle status of a share row. Drives the colored Chip in the table:
 * - `unactivated`: created but the recipient has not OTP-verified.
 * - `activated`:   live, within the trial window.
 * - `expired`:     trial window elapsed (`ActivatedAt + TrialDurationMinutes`).
 * - `revoked`:     `defunctAt` is set (manual revocation by an agent / admin).
 */
type ShareStatus = 'unactivated' | 'activated' | 'expired' | 'revoked';

interface ShareStatusInfo {
  status: ShareStatus;
  label: string;
  color: 'default' | 'success' | 'warning' | 'error';
  /** Up to two timestamp lines rendered under the chip. */
  detail: { label: string; value: string }[];
}

/**
 * Resolve the row's lifecycle status. `defunctAt` always wins (manual
 * revocation overrides everything); otherwise the activation/expiry
 * combination decides between unactivated / activated / expired.
 */
function resolveShareStatus(row: {
  activatedAt: string | null;
  expiresAt: string | null;
  defunctAt: string | null;
  createdAt: string | null;
}): ShareStatusInfo {
  if (row.defunctAt) {
    return {
      status: 'revoked',
      label: 'Revoked',
      color: 'error',
      detail: [{ label: 'Revoked', value: formatTimestamp(row.defunctAt) }],
    };
  }
  if (!row.activatedAt) {
    return {
      status: 'unactivated',
      label: 'Unactivated',
      color: 'default',
      detail: row.createdAt
        ? [{ label: 'Created', value: formatTimestamp(row.createdAt) }]
        : [],
    };
  }
  const expiry = row.expiresAt ? new Date(row.expiresAt) : null;
  const expired = expiry ? expiry.getTime() <= Date.now() : false;
  return {
    status: expired ? 'expired' : 'activated',
    label: expired ? 'Link Expired' : 'Activated',
    color: expired ? 'warning' : 'success',
    detail: [
      { label: 'Activated', value: formatTimestamp(row.activatedAt) },
      ...(expiry
        ? [{ label: expired ? 'Expired' : 'Expires', value: formatTimestamp(row.expiresAt) }]
        : []),
    ],
  };
}

/**
 * Trial period is editable while the link is live (not expired or revoked).
 * Unactivated links may still be configured before the recipient OTPs in.
 */
function canEditTrialPeriod(row: {
  activatedAt: string | null;
  expiresAt: string | null;
  defunctAt: string | null;
  createdAt: string | null;
}): boolean {
  const info = resolveShareStatus(row);
  return info.status !== 'expired' && info.status !== 'revoked';
}

/**
 * Tracks the per-row edit state. Only rows the admin has touched live
 * here — untouched rows render `row.maxTokens` directly from the fetched
 * list. `remainingTokens` is no longer admin-editable; the dialog
 * top-up flow (see `pendingMaxEdit`) derives the new remaining value
 * from the delta on `maxTokens` so the admin can't accidentally desync
 * the two counts.
 */
interface RowEditState {
  maxTokens: string;
  trialAmount: string;
  trialUnit: TrialUnit;
  saving: boolean;
}

/**
 * State for the confirm-max-change dialog. Captured when the admin
 * clicks Save on a row whose `maxTokens` field has changed — the dialog
 * displays the implied remaining-tokens top-up (or reduction) before
 * we commit.
 */
interface PendingMaxEdit {
  row: AdminPickerExternalUser;
  /** Parsed, validated new max value. */
  newMax: number;
  /** `newMax - row.maxTokens`. Positive = top-up, negative = reduction. */
  delta: number;
  /** What `remainingTokens` will become if the admin confirms. */
  newRemaining: number;
  /** When set, applied after (or alongside) the max-tokens update. */
  pendingTrialMinutes?: number;
}

const ManageShareLinksDialog: React.FC<ManageShareLinksDialogProps> = ({
  open,
  onClose,
  mode = 'admin',
}) => {
  const isAdminMode = mode === 'admin';
  const dialogTitle = isAdminMode ? 'Manage Share Links' : 'My Share Links';
  const [mineTab, setMineTab] = useState<MineDialogTab>('shareLinks');
  const [users, setUsers] = useState<AdminPickerExternalUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [edits, setEdits] = useState<Record<number, RowEditState>>({});
  const [copyToast, setCopyToast] = useState<string | null>(null);
  const [successToast, setSuccessToast] = useState<string | null>(null);
  const [pendingMaxEdit, setPendingMaxEdit] = useState<PendingMaxEdit | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (isAdminMode) {
        const data = await api.getAdminPickerExternalUsers();
        setUsers(data.users);
      } else {
        // Agent-side endpoint omits inviting-agent fields (always the
        // caller). Fill them in as nulls so the row shape is uniform —
        // the columns are hidden in `mine` mode anyway.
        const data = await api.getMyPickerExternalUsers();
        setUsers(
          data.users.map((u) => ({
            ...u,
            invitedBy: '',
            invitedByName: null,
          })),
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch external users');
    } finally {
      setLoading(false);
    }
  }, [isAdminMode]);

  // Refetch each time the dialog is opened so the admin sees the
  // current state of share links even if they were created moments ago
  // by a different agent.
  useEffect(() => {
    if (!open) return;
    setMineTab('shareLinks');
    setSearch('');
    setPage(0);
    setEdits({});
    fetchUsers();
  }, [open, fetchUsers]);

  // Free-text search across inviting agent + recipient fields. We
  // deliberately exclude UUIDs from the searchable text — uuids are too
  // noisy to type and the column always renders the full value anyway.
  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter((u) => {
      // In `mine` mode the agent columns are hidden, so only include them
      // in the searchable text when the admin view is active.
      const moduleText = `${u.module ?? ''} ${formatModule(u.module)}`;
      const fields = isAdminMode
        ? [u.invitedByName ?? '', u.invitedBy ?? '', u.name ?? '', u.mobile ?? '', moduleText, u.projectId ?? '']
        : [u.name ?? '', u.mobile ?? '', moduleText, u.projectId ?? ''];
      return fields.join(' ').toLowerCase().includes(query);
    });
  }, [users, search, isAdminMode]);

  const paginatedUsers = useMemo(() => {
    const start = page * ROWS_PER_PAGE;
    return filteredUsers.slice(start, start + ROWS_PER_PAGE);
  }, [filteredUsers, page]);

  /**
   * Lazily create / mutate the edit-state for a row. Existing values fall
   * back to the row's current `maxTokens` so a partial edit on the input
   * doesn't lose state across re-renders.
   */
  const updateEdit = useCallback(
    (row: AdminPickerExternalUser, patch: Partial<RowEditState>) => {
      setEdits((prev) => {
        const trial = trialMinutesToEditState(row.trialDurationMinutes);
        const existing = prev[row.id] ?? {
          maxTokens: String(row.maxTokens),
          trialAmount: trial.amount,
          trialUnit: trial.unit,
          saving: false,
        };
        return { ...prev, [row.id]: { ...existing, ...patch } };
      });
    },
    [],
  );

  const applyTrialUpdateToUsers = useCallback(
    (
      rowId: number,
      result: {
        trialDurationMinutes: number;
        expiresAt: string | null;
        updatedAt: string | null;
      },
    ) => {
      setUsers((prev) =>
        prev.map((u) =>
          u.id === rowId
            ? {
                ...u,
                trialDurationMinutes: result.trialDurationMinutes,
                expiresAt: result.expiresAt,
                updatedAt: result.updatedAt,
              }
            : u,
        ),
      );
    },
    [],
  );

  const commitTrialEdit = useCallback(
    async (row: AdminPickerExternalUser, trialDurationMinutes: number) => {
      updateEdit(row, { saving: true });
      setError(null);
      try {
        const result = await api.updateAdminPickerExternalUserTrial(row.id, {
          trialDurationMinutes,
        });
        applyTrialUpdateToUsers(row.id, result.user);
        setEdits((prev) => {
          const next = { ...prev };
          delete next[row.id];
          return next;
        });
        setSuccessToast(`Updated trial period for ${row.name || row.mobile}`);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update trial period');
        updateEdit(row, { saving: false });
      }
    },
    [applyTrialUpdateToUsers, updateEdit],
  );

  /**
   * Click handler for the per-row Save button. Validates the new max
   * value and, if it differs from the current one, opens the
   * `pendingMaxEdit` confirmation dialog. Actual persistence happens in
   * `commitPendingMaxEdit` once the admin confirms — this keeps the
   * top-up math visible to the admin before any DB write.
   */
  const handleSaveRow = useCallback(
    (row: AdminPickerExternalUser) => {
      const edit = edits[row.id];
      if (!edit) return;

      const maxDirty = edit.maxTokens.trim() !== '' && edit.maxTokens !== String(row.maxTokens);
      const parsedTrial = parseTrialEdit(edit.trialAmount, edit.trialUnit);
      const trialDirty =
        parsedTrial != null && parsedTrial !== row.trialDurationMinutes;

      if (!maxDirty && !trialDirty) {
        setEdits((prev) => {
          const next = { ...prev };
          delete next[row.id];
          return next;
        });
        return;
      }

      if (trialDirty && parsedTrial == null) {
        setError('Trial period must be a positive whole number');
        return;
      }

      if (maxDirty) {
        if (edit.maxTokens.trim() === '') {
          setEdits((prev) => {
            const next = { ...prev };
            delete next[row.id];
            return next;
          });
          return;
        }
        const parsedMax = Number(edit.maxTokens);
        if (!Number.isInteger(parsedMax) || parsedMax < 0) {
          setError('Max tokens must be a non-negative integer');
          return;
        }
        const delta = parsedMax - row.maxTokens;
        const newRemaining =
          delta > 0 ? row.remainingTokens + delta : Math.min(row.remainingTokens, parsedMax);
        setPendingMaxEdit({
          row,
          newMax: parsedMax,
          delta,
          newRemaining,
          pendingTrialMinutes: trialDirty ? parsedTrial ?? undefined : undefined,
        });
        return;
      }

      if (trialDirty && parsedTrial != null) {
        void commitTrialEdit(row, parsedTrial);
      }
    },
    [commitTrialEdit, edits],
  );

  /**
   * Persist the pending max-tokens change after the admin confirms. We
   * always send both `maxTokens` and `remainingTokens` so the backend's
   * row is left in a consistent state regardless of which direction the
   * admin moved the max.
   */
  const commitPendingMaxEdit = useCallback(async () => {
    if (!pendingMaxEdit) return;
    const { row, newMax, newRemaining, pendingTrialMinutes } = pendingMaxEdit;
    updateEdit(row, { saving: true });
    setError(null);
    try {
      const result = await api.updateAdminPickerExternalUserTokens(row.id, {
        maxTokens: newMax,
        remainingTokens: newRemaining,
      });
      setUsers((prev) =>
        prev.map((u) =>
          u.id === row.id
            ? {
                ...u,
                maxTokens: result.user.maxTokens,
                remainingTokens: result.user.remainingTokens,
                updatedAt: result.user.updatedAt,
              }
            : u,
        ),
      );

      if (pendingTrialMinutes != null) {
        const trialResult = await api.updateAdminPickerExternalUserTrial(row.id, {
          trialDurationMinutes: pendingTrialMinutes,
        });
        applyTrialUpdateToUsers(row.id, trialResult.user);
      }

      setEdits((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
      setSuccessToast(
        pendingTrialMinutes != null
          ? `Updated tokens and trial period for ${row.name || row.mobile}`
          : `Updated tokens for ${row.name || row.mobile}`,
      );
      setPendingMaxEdit(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update share link');
      updateEdit(row, { saving: false });
      setPendingMaxEdit(null);
    }
  }, [applyTrialUpdateToUsers, pendingMaxEdit, updateEdit]);

  const handleCopyShareLink = useCallback(async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopyToast('Share link copied');
    } catch {
      setCopyToast('Failed to copy share link');
    }
  }, []);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
          {dialogTitle}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {(isAdminMode || mineTab === 'shareLinks') && (
            <Tooltip title="Refresh">
              <span>
                <IconButton size="small" onClick={fetchUsers} disabled={loading}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {!isAdminMode && (
          <Tabs
            value={mineTab}
            onChange={(_, value: MineDialogTab) => setMineTab(value)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value="shareLinks" label="Share Links" />
            <Tab value="tokenUsage" label="Token Usage Analytics" />
          </Tabs>
        )}

        {!isAdminMode && mineTab === 'tokenUsage' ? (
          <TokenUsageAnalyticsDialog
            open={open}
            onClose={onClose}
            mode="agent"
            embedded
          />
        ) : (
        <>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
          <TextField
            size="small"
            placeholder={
              isAdminMode
                ? 'Search by agent, recipient, mobile, module…'
                : 'Search by recipient, mobile, module…'
            }
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ flex: 1, maxWidth: 480 }}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {filteredUsers.length} of {users.length} share{users.length === 1 ? '' : 's'}
          </Typography>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: '60vh' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {isAdminMode && (
                    <>
                      <TableCell sx={{ fontWeight: 600 }}>Agent Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Agent CEA</TableCell>
                    </>
                  )}
                  <TableCell sx={{ fontWeight: 600 }}>Recipient Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Phone Number</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Module</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Share Link</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    <Box
                      component="span"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <TokenIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                      Remaining
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    <Box
                      component="span"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <TokenIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                      Spent
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    <Box
                      component="span"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <TokenIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                      Max
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>Trial Period</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  {isAdminMode && (
                    <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsers.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={isAdminMode ? 12 : 9}
                      align="center"
                      sx={{ py: 4, color: 'text.secondary' }}
                    >
                      No share links found.
                    </TableCell>
                  </TableRow>
                )}
                {paginatedUsers.map((row) => {
                  const edit = edits[row.id];
                  const trialDefaults = trialMinutesToEditState(row.trialDurationMinutes);
                  const maxValue = edit?.maxTokens ?? String(row.maxTokens);
                  const trialAmount = edit?.trialAmount ?? trialDefaults.amount;
                  const trialUnit = edit?.trialUnit ?? trialDefaults.unit;
                  // `Spent` is always derived from the persisted row — we
                  // don't try to preview the spent count off the in-flight
                  // edit, since spent doesn't change when max changes.
                  const spent = Math.max(0, row.maxTokens - row.remainingTokens);
                  const maxDirty = Boolean(
                    edit && edit.maxTokens.trim() !== '' && edit.maxTokens !== String(row.maxTokens),
                  );
                  const parsedTrial = parseTrialEdit(trialAmount, trialUnit);
                  const trialDirty = Boolean(
                    edit && parsedTrial != null && parsedTrial !== row.trialDurationMinutes,
                  );
                  const isDirty = maxDirty || trialDirty;
                  const trialEditable = isAdminMode && canEditTrialPeriod(row);
                  return (
                    <TableRow key={row.id} hover>
                      {isAdminMode && (
                        <>
                          <TableCell>{row.invitedByName ?? '—'}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                            {row.invitedBy}
                          </TableCell>
                        </>
                      )}
                      <TableCell>{row.name ?? '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                        {row.mobile}
                      </TableCell>
                      <TableCell>{formatModule(row.module)}</TableCell>
                      <TableCell sx={{ minWidth: 280 }}>
                        {(() => {
                          // Prefer the persisted shortened URL. If the
                          // backend couldn't shorten at create time the
                          // column is `null` — fall back to the raw uuid
                          // so admins can still copy something useful
                          // (the agent's app reconstructs the full URL
                          // from a uuid anyway).
                          const displayLink = row.shortUrl ?? row.uuid;
                          return (
                            <Tooltip title={displayLink}>
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  maxWidth: '100%',
                                }}
                              >
                                <Box
                                  component="a"
                                  href={row.shortUrl ?? undefined}
                                  target={row.shortUrl ? '_blank' : undefined}
                                  rel={row.shortUrl ? 'noopener noreferrer' : undefined}
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.72rem',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    minWidth: 0,
                                    color: row.shortUrl ? 'primary.main' : 'text.primary',
                                    textDecoration: row.shortUrl ? 'underline' : 'none',
                                  }}
                                >
                                  {displayLink}
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyShareLink(displayLink)}
                                  sx={{ p: 0.25, flexShrink: 0 }}
                                >
                                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Box>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      {/* Remaining and Spent are always read-only — admins
                          adjust both indirectly by changing Max (which
                          triggers the confirm-and-top-up dialog). */}
                      <TableCell align="right">{row.remainingTokens}</TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary' }}>
                        {spent}
                      </TableCell>
                      {isAdminMode ? (
                        <TableCell align="right" sx={{ width: 110 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={maxValue}
                            onChange={(e) =>
                              updateEdit(row, { maxTokens: e.target.value })
                            }
                            inputProps={{ min: 0, style: { textAlign: 'right', width: 64 } }}
                            disabled={edit?.saving}
                            InputProps={{
                              endAdornment: edit?.saving ? (
                                <InputAdornment position="end">
                                  <CircularProgress size={12} />
                                </InputAdornment>
                              ) : undefined,
                            }}
                          />
                        </TableCell>
                      ) : (
                        <TableCell align="right">{row.maxTokens}</TableCell>
                      )}
                      <TableCell sx={{ minWidth: 180 }}>
                        {trialEditable ? (
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <TextField
                              size="small"
                              type="number"
                              value={trialAmount}
                              onChange={(e) =>
                                updateEdit(row, { trialAmount: e.target.value })
                              }
                              inputProps={{ min: 1, style: { width: 52, textAlign: 'right' } }}
                              disabled={edit?.saving}
                            />
                            <Select
                              size="small"
                              value={trialUnit}
                              onChange={(e) =>
                                updateEdit(row, {
                                  trialUnit: e.target.value as TrialUnit,
                                })
                              }
                              disabled={edit?.saving}
                              sx={{ minWidth: 92 }}
                            >
                              <MenuItem value="days">day(s)</MenuItem>
                              <MenuItem value="hours">hour(s)</MenuItem>
                            </Select>
                          </Stack>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {formatTrialDuration(row.trialDurationMinutes)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: 170 }}>
                        {(() => {
                          const info = resolveShareStatus(row);
                          return (
                            <Stack spacing={0.25} alignItems="flex-start">
                              <Chip
                                label={info.label}
                                size="small"
                                color={info.color}
                                variant={info.color === 'default' ? 'outlined' : 'filled'}
                                sx={{ fontWeight: 600 }}
                              />
                              {info.detail.map((d) => (
                                <Typography
                                  key={d.label}
                                  variant="caption"
                                  sx={{ color: 'text.secondary', lineHeight: 1.3 }}
                                >
                                  {d.label}: {d.value}
                                </Typography>
                              ))}
                            </Stack>
                          );
                        })()}
                      </TableCell>
                      {isAdminMode && (
                        <TableCell align="center" sx={{ width: 100 }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon fontSize="small" />}
                            disabled={!isDirty || edit?.saving}
                            onClick={() => handleSaveRow(row)}
                            sx={{ minWidth: 0, px: 1 }}
                          >
                            Save
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          component="div"
          count={filteredUsers.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[ROWS_PER_PAGE]}
        />
        </>
        )}
      </DialogContent>

      {/*
        Confirm-and-top-up dialog. Shown whenever the admin presses Save
        on a row with a changed Max value. Explains the implied
        remaining-tokens adjustment so admins can't unintentionally
        top-up or reduce a recipient's allowance.
      */}
      <Dialog
        open={Boolean(pendingMaxEdit)}
        onClose={() => {
          if (pendingMaxEdit && edits[pendingMaxEdit.row.id]?.saving) return;
          setPendingMaxEdit(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm max tokens change</DialogTitle>
        <DialogContent>
          {pendingMaxEdit && (() => {
            const { row, newMax, delta, newRemaining, pendingTrialMinutes } = pendingMaxEdit;
            const recipient = row.name || row.mobile;
            const absDelta = Math.abs(delta);
            const increasing = delta > 0;
            return (
              <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                <DialogContentText sx={{ color: 'text.primary' }}>
                  Change <strong>{recipient}</strong>’s max tokens from{' '}
                  <strong>{row.maxTokens}</strong> to <strong>{newMax}</strong>?
                </DialogContentText>
                {pendingTrialMinutes != null && (
                  <DialogContentText sx={{ color: 'text.primary' }}>
                    Also update trial period to{' '}
                    <strong>{formatTrialDuration(pendingTrialMinutes)}</strong>?
                  </DialogContentText>
                )}
                <Alert severity={increasing ? 'info' : 'warning'} variant="outlined">
                  {increasing ? (
                    <>
                      Increasing max tokens by <strong>{absDelta}</strong> will top up{' '}
                      remaining tokens by <strong>{absDelta}</strong> (from{' '}
                      <strong>{row.remainingTokens}</strong> to{' '}
                      <strong>{newRemaining}</strong>).
                    </>
                  ) : newRemaining < row.remainingTokens ? (
                    <>
                      Decreasing max tokens will clamp remaining tokens from{' '}
                      <strong>{row.remainingTokens}</strong> down to{' '}
                      <strong>{newRemaining}</strong> (the new max).
                    </>
                  ) : (
                    <>
                      Decreasing max tokens to <strong>{newMax}</strong>. Remaining{' '}
                      tokens (<strong>{row.remainingTokens}</strong>) stays unchanged{' '}
                      since it already fits within the new max.
                    </>
                  )}
                </Alert>
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPendingMaxEdit(null)}
            disabled={
              pendingMaxEdit ? edits[pendingMaxEdit.row.id]?.saving : false
            }
          >
            Cancel
          </Button>
          <Button
            onClick={commitPendingMaxEdit}
            variant="contained"
            color="primary"
            disabled={
              pendingMaxEdit ? edits[pendingMaxEdit.row.id]?.saving : false
            }
            startIcon={
              pendingMaxEdit && edits[pendingMaxEdit.row.id]?.saving ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(copyToast)}
        autoHideDuration={2500}
        onClose={() => setCopyToast(null)}
        message={copyToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <Snackbar
        open={Boolean(successToast)}
        autoHideDuration={2500}
        onClose={() => setSuccessToast(null)}
        message={successToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  );
};

export default ManageShareLinksDialog;
